import { useParams, Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ComicCard from '@/components/feature/ComicCard';
import { comics, categories } from '@/mocks/comics';
import type { Comic } from '@/types/comic';

export default function ComicDetail() {
  const { slug } = useParams<{ slug: string }>();
  const comic = comics.find((c) => c.slug === slug);
  const [reading, setReading] = useState(false);
  const [currentChapter, setCurrentChapter] = useState<number | null>(null);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-20 pb-16">
          <div className="px-4 md:px-6 max-w-screen-2xl mx-auto text-center py-20">
            <i className="ri-error-warning-line text-4xl text-foreground-300 w-10 h-10 inline-flex items-center justify-center"></i>
            <p className="mt-3 text-foreground-500">漫画未找到</p>
            <Link to="/" className="mt-4 inline-block text-sm text-primary-500 hover:text-primary-600 cursor-pointer whitespace-nowrap">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const cat = categories.find((c) => c.slug === comic.category);
  const relatedComics = comics
    .filter((c) => c.category === comic.category && c.id !== comic.id)
    .slice(0, 5);

  const chapterIdx = comic.chapterList.findIndex((ch) => ch.number === currentChapter);

  const formatViews = (v: number) => {
    if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
    return v.toString();
  };

  const startReading = (chapterNum: number) => {
    setCurrentChapter(chapterNum);
    setReading(true);
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  return (
    <>
      <Navbar />
      <main className="pt-20 pb-16">
        <div className="px-4 md:px-6 max-w-screen-2xl mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: cat ? `${cat.name}漫画` : '分类', href: cat ? `/category/${cat.slug}` : '/category/all' },
              { label: comic.title },
            ]}
          />

          {/* Comic Info Section */}
          <div className="mt-6 flex flex-col md:flex-row gap-8">
            {/* Cover */}
            <div className="flex-shrink-0 w-40 md:w-56">
              <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-100">
                <img
                  src={comic.coverImage}
                  alt={comic.title}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                {comic.title}
              </h1>
              <div className="flex flex-wrap items-center gap-3 mt-3">
                <span className="text-sm text-foreground-600">作者：{comic.author}</span>
                <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-label ${
                  comic.status === '连载中'
                    ? 'bg-primary-100 text-primary-700'
                    : 'bg-accent-100 text-accent-700'
                }`}>
                  {comic.status}
                </span>
                <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                <span className="text-sm text-accent-500 font-medium">
                  <i className="ri-star-fill w-4 h-4 inline-flex items-center justify-center mr-0.5"></i>
                  {comic.rating}
                </span>
                <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                <span className="text-sm text-foreground-500">
                  <i className="ri-eye-line w-4 h-4 inline-flex items-center justify-center mr-0.5"></i>
                  {formatViews(comic.views)}
                </span>
                <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                <span className="text-sm text-foreground-500">共 {comic.chapters} 话</span>
              </div>

              <div className="flex flex-wrap gap-1.5 mt-3">
                {comic.tags.map((tag) => (
                  <span key={tag} className="text-xs px-2 py-0.5 rounded bg-secondary-100 text-secondary-700 font-label">
                    {tag}
                  </span>
                ))}
              </div>

              <p className="mt-4 text-sm text-foreground-600 leading-relaxed max-w-xl">
                {comic.description}
              </p>

              <div className="flex items-center gap-3 mt-5">
                <button
                  onClick={() => startReading(comic.chapterList[0]?.number || 1)}
                  className="whitespace-nowrap px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-label font-medium hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  <i className="ri-play-circle-line w-4 h-4 inline-flex items-center justify-center mr-1.5"></i>
                  开始阅读
                </button>
                <button
                  onClick={() => startReading(comic.chapterList[comic.chapterList.length - 1]?.number || 1)}
                  className="whitespace-nowrap px-6 py-2.5 bg-background-100 text-foreground-700 rounded-full text-sm font-label hover:bg-background-200 transition-colors cursor-pointer"
                >
                  最新话
                </button>
              </div>
            </div>
          </div>

          {/* Reading Area or Chapter List */}
          <div className="mt-10">
            {reading && currentChapter ? (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <button
                    onClick={() => setReading(false)}
                    className="whitespace-nowrap flex items-center gap-1.5 text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer"
                  >
                    <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
                    返回详情
                  </button>
                  <h2 className="text-lg font-heading font-bold text-foreground-900">
                    {comic.title} - 第{currentChapter}话
                  </h2>
                  <div className="flex items-center gap-2">
                    {chapterIdx >= 0 && chapterIdx < comic.chapterList.length - 1 && (
                      <button
                        onClick={() => startReading(comic.chapterList[chapterIdx + 1].number)}
                        className="whitespace-nowrap px-3 py-1.5 text-xs bg-background-100 text-foreground-600 rounded-full hover:bg-background-200 transition-colors cursor-pointer"
                      >
                        <i className="ri-arrow-up-s-line w-4 h-4 inline-flex items-center justify-center mr-1"></i>
                        上一话
                      </button>
                    )}
                    {chapterIdx > 0 && (
                      <button
                        onClick={() => startReading(comic.chapterList[chapterIdx - 1].number)}
                        className="whitespace-nowrap px-3 py-1.5 text-xs bg-primary-500 text-background-50 rounded-full hover:bg-primary-600 transition-colors cursor-pointer"
                      >
                        下一话
                        <i className="ri-arrow-down-s-line w-4 h-4 inline-flex items-center justify-center ml-1"></i>
                      </button>
                    )}
                  </div>
                </div>

                {/* Reading Content - Simulated */}
                <div className="max-w-2xl mx-auto bg-background-100 rounded-xl p-8 text-center">
                  <div className="space-y-6">
                    <div className="border-2 border-dashed border-background-300 rounded-lg p-12">
                      <i className="ri-image-line text-5xl text-foreground-300 w-12 h-12 inline-flex items-center justify-center"></i>
                      <p className="mt-3 text-foreground-500 text-sm">第{currentChapter}话漫画内容</p>
                      <p className="text-xs text-foreground-400 mt-1">
                        {comic.chapterList.find((ch) => ch.number === currentChapter)?.title || ''}
                      </p>
                    </div>
                    <p className="text-xs text-foreground-400">
                      下拉式阅读模式 - 滚动查看更多内容
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <h3 className="text-lg font-heading font-bold text-foreground-900 mb-4 flex items-center gap-2">
                  <i className="ri-list-check w-5 h-5 flex items-center justify-center text-primary-500"></i>
                  章节列表
                  <span className="text-sm font-normal text-foreground-500 font-body">（下拉式阅读 · 共{comic.chapters}话）</span>
                </h3>

                <div className="bg-background-100 rounded-xl overflow-hidden">
                  <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-2.5 text-xs text-foreground-500 font-label border-b border-background-200/70">
                    <div className="col-span-2">话数</div>
                    <div className="col-span-7">标题</div>
                    <div className="col-span-3">更新日期</div>
                  </div>
                  {comic.chapterList.map((ch) => (
                    <button
                      key={ch.id}
                      onClick={() => startReading(ch.number)}
                      className="w-full text-left grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-5 py-3 items-center border-b border-background-200/50 last:border-b-0 hover:bg-background-200/50 transition-colors cursor-pointer"
                    >
                      <div className="col-span-2">
                        <span className="text-sm font-medium text-foreground-900">
                          第{ch.number}话
                        </span>
                      </div>
                      <div className="col-span-7">
                        <span className="text-sm text-foreground-700 line-clamp-1">{ch.title}</span>
                      </div>
                      <div className="col-span-3 text-xs text-foreground-500">{ch.date}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Related Comics */}
          {!reading && relatedComics.length > 0 && (
            <div className="mt-14">
              <h3 className="text-lg font-heading font-bold text-foreground-900 mb-4">
                <i className="ri-links-line w-5 h-5 inline-flex items-center justify-center text-accent-500 mr-2"></i>
                相关推荐
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                {relatedComics.map((c) => (
                  <ComicCard key={c.id} comic={c} />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* SEO */}
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Article',
              headline: `${comic.title} - 韩漫大全`,
              author: { '@type': 'Person', name: comic.author },
              description: comic.description,
              url: window.location.href,
              image: comic.coverImage,
              publisher: {
                '@type': 'Organization',
                name: '韩漫大全',
              },
              datePublished: comic.updatedAt,
              dateModified: comic.updatedAt,
            }),
          }}
        />
      </main>
      <Footer />
    </>
  );
}